import PropTypes from "prop-types"
import React, { useEffect, useRef, useState } from "react"
import SimpleBar from "simplebar-react"
import withRouter from "components/Common/withRouter"
import { Link, useLocation } from "react-router-dom"
import { withTranslation } from "react-i18next"
import axios from "axios"
import { set_user_menu_permission, setCookie } from "utils/CommonFunctions"

const MENU_CACHE_KEY = "sidebarMenuDataCache"
const PERM_CACHE_KEY = "sidebarPermCache"

const getCachedMenu = () => {
  try {
    return JSON.parse(sessionStorage.getItem(MENU_CACHE_KEY)) || []
  } catch {
    return []
  }
}

const getCachedPerm = () => {
  try {
    return (
      JSON.parse(sessionStorage.getItem(PERM_CACHE_KEY)) || {
        parentIds: [],
        childIds: []
      }
    )
  } catch {
    return { parentIds: [], childIds: [] }
  }
}

const menuMatchesPath = (menu, pathName) => {
  if (!menu.sub_menus) return false
  return menu.sub_menus.some(subMenu => {
    const pageName = (subMenu?.page_name || "").replace(/\/$/, "")
    if (pageName === pathName) return true
    // Keep parent menu open for related child routes
    if (pageName && pathName.startsWith(pageName)) return true
    return false
  })
}

// Some page_names are registered under more than one parent (e.g.
// "/recruiter-report" exists under both "Job Orders" and "Jobs").
// If the currently-open parent still matches, stay there instead of
// jumping to whichever duplicate happens to appear later in menuData.
const computeActiveParentId = (menuData, pathName, preferredMenuId) => {
  if (preferredMenuId != null) {
    const preferredMenu = menuData.find(menu => menu.menu_id === preferredMenuId)
    if (preferredMenu && menuMatchesPath(preferredMenu, pathName)) {
      return preferredMenuId
    }
  }

  const matched = menuData.find(menu => menuMatchesPath(menu, pathName))
  return matched ? matched.menu_id : null
}

const SidebarContent = props => {
  const location = useLocation()
  const ref = useRef()

  const cachedMenu = getCachedMenu()
  const cachedPerm = getCachedPerm()
  const cleanInitialPath = (location.pathname || "").replace(/\/$/, "")

  const [menuData, setMenuData] = useState(cachedMenu)
  const [menuLoading, setMenuLoading] = useState(cachedMenu.length === 0)
  const [permissionLoading, setPermissionLoading] = useState(
    cachedPerm.parentIds.length === 0
  )
  // Compute synchronously on mount so a remount doesn't flash the menu closed
  const [activeMenu, setActiveMenu] = useState(() =>
    cachedMenu.length ? computeActiveParentId(cachedMenu, cleanInitialPath, null) : null
  )
  const [userParentModules, setUserParentModules] = useState(cachedPerm.parentIds)
  const [userChildModules, setUserChildModules] = useState(cachedPerm.childIds)

  // Fetch menu data from API
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const response = await axios.get(`/api/v1/get-menu-data/`)
        const results = response.data.results || []
        setMenuData(results)
        try {
          sessionStorage.setItem(MENU_CACHE_KEY, JSON.stringify(results))
        } catch {
          // storage full/unavailable - ignore, fetch still succeeded
        }
      } catch (error) {
        console.error("Error fetching menu data:", error)
      } finally {
        setMenuLoading(false)
      }
    }
    fetchMenuData()
  }, [])

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await axios.get(`/api/v1/users/user_permissions/`)
        localStorage.setItem("menuLoaded", true)

        const permissions = response.data.permissions
        const parentIds = permissions[0].parent_id.split(",").map(Number)
        const childIds = permissions[0].child_id.split(",").map(Number)

        setUserParentModules(parentIds)
        setUserChildModules(childIds)

        setCookie("parentModules", JSON.stringify(parentIds))
        setCookie("childModules", JSON.stringify(childIds))
        try {
          sessionStorage.setItem(
            PERM_CACHE_KEY,
            JSON.stringify({ parentIds, childIds })
          )
        } catch {
          // storage full/unavailable - ignore
        }

        const permissionConfig = {
          add: permissions[0].add?.split(",").map(Number) || [],
          edit: permissions[0].edit?.split(",").map(Number) || [],
          delete: permissions[0].delete?.split(",").map(Number) || [],
          view: permissions[0].view?.split(",").map(Number) || []
        }

        set_user_menu_permission(
          permissionConfig.add,
          permissionConfig.edit,
          permissionConfig.delete,
          permissionConfig.view
        )
      } catch (error) {
        console.error("Error fetching permissions:", error)
      } finally {
        setPermissionLoading(false)
      }
    }

    fetchPermissions()
  }, [])

  const showSidebarShimmer = menuLoading || permissionLoading

  // Set active menu based on pathname. Only OPENS the matching parent -
  // never force-closes when no match is found (e.g. transient empty menuData).
  useEffect(() => {
    const pathName = (location.pathname || "").replace(/\/$/, "")

    setActiveMenu(prevActiveMenu => {
      const activeParentId = computeActiveParentId(menuData, pathName, prevActiveMenu)
      return activeParentId || prevActiveMenu
    })
  }, [location.pathname, menuData])

  // Recalculate scrollbar
  useEffect(() => {
    if (ref.current) {
      ref.current.recalculate()
    }
  }, [menuData])

  // Scroll to active menu item
  const scrollElement = item => {
    if (item && ref.current) {
      const currentPosition = item.offsetTop
      if (currentPosition > window.innerHeight) {
        ref.current.getScrollElement().scrollTop = currentPosition - 300
      }
    }
  }

  // Toggle menu
  const toggleMenu = (e, menuId) => {
    e.preventDefault()
    setActiveMenu(prev => (prev === menuId ? null : menuId))
  }

  // Safe submenu active check
  const isSubMenuActive = (subMenus, pathName) => {
    const cleanPath = (pathName || "").replace(/\/$/, "")
    return subMenus?.some(subMenu => {
      const page = subMenu?.page_name || ""
      return page.replace(/\/$/, "") === cleanPath
    })
  }

  // Filter menu by permission
  const filteredMenuData = menuData
    .filter(menu => userParentModules.includes(Number(menu.menu_id)))
    .map(menu => ({
      ...menu,
      sub_menus:
        menu.sub_menus?.filter(subMenu =>
          userChildModules.includes(Number(subMenu.menu_id))
        ) || []
    }))
    .filter(
      menu =>
        (menu.sub_menus && menu.sub_menus.length > 0) ||
        menu.page_name ||
        menu.sub_menus?.length === 0
    )
    .sort((a, b) => {
      const isAtsdashboard = menu =>
        (menu.page_name || "").replace(/\/$/, "") === "/ats-dashboard" ||
        (menu.menu_name || "").toLowerCase().includes("ats dashboard") ||
        (menu.menu_name || "").toLowerCase().includes("manager dashboard")

      if (isAtsdashboard(a) && !isAtsdashboard(b)) return -1
      if (!isAtsdashboard(a) && isAtsdashboard(b)) return 1
      return 0
    })

  return (
    <React.Fragment>
      <SimpleBar style={{ maxHeight: "100%" }} ref={ref}>
        <div id="sidebar-menu">
          {showSidebarShimmer ? (
            <div className="sidebar-shimmer" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="sidebar-shimmer__item">
                  <div className="sidebar-shimmer__icon" />
                  <div className="sidebar-shimmer__label" />
                </div>
              ))}
            </div>
          ) : (
          <ul className="metismenu list-unstyled" id="side-menu">
            {filteredMenuData.map(menu => {
              const cleanPath = (location.pathname || "").replace(/\/$/, "")
              const isCalendarMenu = (menu.menu_name || "").toLowerCase().includes("calendar")
              const isEmailsMenu = (menu.menu_name || "").toLowerCase().includes("email")
              const hasSubMenus = !isCalendarMenu && menu.sub_menus && menu.sub_menus.length > 0
              const isOpen = activeMenu === menu.menu_id
              const isActive = hasSubMenus
                ? isSubMenuActive(menu.sub_menus, cleanPath)
                : (menu.page_name || "").replace(/\/$/, "") === cleanPath

              if (isEmailsMenu) {
                const emailLink = "/email-module"
                const isEmailActive = emailLink === cleanPath
                return (
                  <li key={menu.menu_id} className={isEmailActive ? "mm-active" : ""}>
                    <Link
                      to={emailLink}
                      className={`waves-effect ${isEmailActive ? "active" : ""}`}
                    >
                      {menu.menu_icons && (
                        <i className={`${menu.menu_icons} ${isEmailActive ? "icon-active" : ""}`}></i>
                      )}
                      <span>{props.t(menu.menu_name)}</span>
                    </Link>
                    {/*
                      Email child menu is intentionally hidden for now.
                      Keep API children available here for future restore:
                      {menu.sub_menus?.map(subMenu => subMenu.menu_name).join(", ")}
                    */}
                  </li>
                )
              }

              // Standalone menu item (no sub_menus) - render as direct link
              const standalonePageMap = {
                "Settings": "/settings",
                "Agent Creation": "/agentList",
                "Telephonic Tests": "/telephonic-tests",
              }
              if (!hasSubMenus) {
                const menuLink = isCalendarMenu
                  ? "/my-calendar"
                  : menu.page_name || standalonePageMap[menu.menu_name] || "/#"
                const isStandaloneActive = menuLink.replace(/\/$/, "") === cleanPath
                return (
                  <li key={menu.menu_id} className={isStandaloneActive ? "mm-active" : ""}>
                    <Link
                      to={menuLink}
                      className={`waves-effect ${isStandaloneActive ? "active" : ""}`}
                    >
                      {menu.menu_icons && (
                        <i className={`${menu.menu_icons} ${isActive ? "icon-active" : ""}`}></i>
                      )}
                      <span>{props.t(menu.menu_name)}</span>
                    </Link>
                  </li>
                )
              }

              return (
                <li key={menu.menu_id} className={isActive || isOpen ? "mm-active" : ""}>
                  <Link
                    to="/#"
                    className={`has-arrow waves-effect ${
                      isActive || isOpen ? "active" : ""
                    }`}
                    onClick={e => toggleMenu(e, menu.menu_id)}
                    ref={node => {
                      if (isActive && node) scrollElement(node)
                    }}
                  >
                    {menu.menu_icons && (
                      <i
                        className={`${menu.menu_icons} ${
                          isActive || isOpen ? "icon-active" : ""
                        }`}
                      ></i>
                    )}
                    <span>{props.t(menu.menu_name)}</span>
                  </Link>

                  {/* SUB MENU */}
                  {menu.sub_menus && menu.sub_menus.length > 0 && (
                    <ul
                      className={`sub-menu ${isOpen ? "mm-show" : "mm-collapse"}`}
                      aria-expanded={isOpen}
                    >
                      {menu.sub_menus.map(subMenu => {
                        const page = subMenu?.page_name || (String(subMenu?.menu_name || "").trim().toLowerCase() === "settings" ? "/settings" : "")
                        const cleanMenuPage = page.replace(/\/$/, "")
                        const isActiveSub = cleanPath === cleanMenuPage

                        return (
                          <li
                            key={subMenu.menu_id}
                            className={isActiveSub ? "mm-active" : ""}
                          >
                            <Link
                              to={page || "/#"}
                              className={isActiveSub ? "active" : ""}
                            >
                              {props.t(subMenu.menu_name)}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
          )}
        </div>
      </SimpleBar>
    </React.Fragment>
  )
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any
}

export default withRouter(withTranslation()(SidebarContent))
